import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import AppDesignerSidebarDialogHeader from './SidebarContentSettings/AppDesignerSidebarDialogHeader';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Dropdown,
    DropdownItem,
    Typography,
    Popper,
    makeStyles,
    IconButton
} from '@ellucian/react-design-system/core';
import { Icon } from '@ellucian/ds-icons/lib';
import { FormattedMessage, useIntl } from 'react-intl';
import QueryBuilder from '@nsr/react-query-builder';
import get from 'lodash/get';
import { CONSTANTS } from '../Helpers/Configuration';
import { attributeTypes, operators as operatorsConfig } from '@nsr/react-query-builder-executor';
import {
    spacing40
} from '@ellucian/react-design-system/core/styles/tokens';
import { fetchDynamicListForQuestion } from '../Helpers/dataAccessFetch';
import { useApi } from '../ApiContext/AdminApiContext';
import { useSnackbar } from '../SnackbarContext/SnackbarContext';
import { useSelector } from 'react-redux';

import {
    selectApplicationQuestions,
    selectApplicationPages,
    selectApplicationSections,
    selectSelectedControl
} from '../../redux/admin/slices/templateSlice';
import { selectTenant } from '../../redux/admin/slices/adminBaseSlice';
const useStyles = makeStyles(() => ({
    paper: {
        height: '100vh',
        maxWidth: '100vw',
        width: ['100vw', '!important'],
    },
    dropDownContainer: {
        display: 'grid',
        maxWidth: '40%',
        alignItems: 'center',
        gridColumnGap: spacing40,
        gridTemplateColumns: 'auto max-content',
    },
    popper: {
        zIndex: ['1500', '!important'],
        whiteSpace: 'pre-line'
    },
    titleContainer: {
        display: 'inline-grid',
        gridTemplateColumns: 'max-content max-content',
        alignItems: 'center',
    }
}));

export const ContentActionOptions = {
    required: 'required',
    show: 'show',
    hide: 'hide'
};

export const ConditionsSchema = {
    effect: 'effect',
};

const { controlTypes, appTemplateControlTypes } = CONSTANTS;
const ConditionalLogicDialog = props => {
    const {
        initialQuery: initialQueryProp,
        onClose,
        onSave: onSaveProp,
        open,
        conditionIndex
    } = props;
    const intl = useIntl();
    const classes = useStyles();
    const api = useApi();
    const snackbar = useSnackbar();
    const [query, setQuery] = useState(null);
    const [contentActionDropDownOpen, setContentActionDropDownOpen] = useState(false);

    // redux 
    const applicationQuestions = useSelector(selectApplicationQuestions);
    const applicationPages = useSelector(selectApplicationPages);
    const applicationSections = useSelector(selectApplicationSections);
    const selectedControl = useSelector(selectSelectedControl);
    const { supportedGraphqlModels } = useSelector(selectTenant);

    const [contentActionDropDownValue, setContentActionDropDownValue] = useState(ContentActionOptions.show);
    const [contentActionDropDownOptions, setContentActionDropDownOptions] = useState(ContentActionOptions);
    const [helpPopperAnchorEl, setAnchorEl] = useState(null);
    const [, updateEnums] = useState(false);

    const getCurrentControl = () => {
        if (selectedControl.controlType === appTemplateControlTypes.page) {
            return applicationPages.find(p => p.id === selectedControl.pageId);
        }
        else if (selectedControl.controlType === appTemplateControlTypes.section) {
            return applicationSections.find(s => s.id === selectedControl.sectionId);
        }
        else if (selectedControl.controlType === appTemplateControlTypes.question) {
            return applicationQuestions.find(q => q.id === selectedControl.questionId);
        }
    };
    const currentControl = getCurrentControl();

    useEffect(() => {
        setQuery(initialQueryProp);
        initializeDropDown();
    }, [initialQueryProp]);

    let initialQuery = initialQueryProp;
    if (!initialQuery) {
        initialQuery = { groups: [], where: [] };
    }

    const fetchAttributeSuggestions = (term, requestId) => {
        return new Promise((resolve) => {
            const validQuestions = applicationQuestions.filter(it => {
                return (
                    ![controlTypes.daterange, controlTypes.empty].includes(it.type)
                    && ![it.pageId, it.sectionId, it.id].includes(currentControl.id)
                    && it.label
                );
            });
            const matches = validQuestions.filter((it) => it.label.match(new RegExp(term, 'i')));
            const suggestions = matches.map(it => {
                let type = attributeTypes.string;
                let operators = operatorsConfig[type];
                let enumValues;
                if (it.type === controlTypes.datepicker) {
                    type = attributeTypes.date;
                    operators = operatorsConfig[type];
                }
                else if ([ controlTypes.singlechoice, controlTypes.checkbox, controlTypes.searchandselect].includes(it.type)) {
                    type = attributeTypes.multiSelectEnum;
                    operators = operatorsConfig[type];
                    enumValues = get(it.possibleValues, 'values', []).map(v => {
                        return {
                            label: v.label || v.value,
                            value: v.value,
                        };
                    });
                }                
                else if ([controlTypes.radiogroup].includes(it.type)) {
                    type = attributeTypes.radioChoiceEnum;
                    operators = operatorsConfig[type];
                    enumValues = get(it.possibleValues, 'values', []).map(v => {
                        return {
                            label: v.label || v.value,
                            value: v.value,
                        };
                    });
                }
                operators = operators.map(it => ({
                    hideValueInput: it.hideValueInput,
                    label: intl.formatMessage({ id: it.label }),
                    value: it.value,
                }));
                const model = {
                    label: it.label,
                    operators,
                    // path: [{ label: 'Name A' }, { label: 'Name B' }, { label: 'Name C' }],
                    type,
                    value: it,
                    isLoading: false,
                };
                if (type === attributeTypes.enum || type === attributeTypes.multiSelectEnum || type === attributeTypes.radioChoiceEnum) {
                    model.enum = enumValues;
                }
                return model;
            });
            resolve({
                requestId,
                suggestions,
            });
        });
    };

    const populateAttributeForDynamicValues = async (attribute) => {
        if (attribute?.value?.possibleValues.type === CONSTANTS.questionPossibleValueTypes.dynamic &&
            (attribute.type === attributeTypes.enum || attribute.type === attributeTypes.multiSelectEnum || attribute.type === attributeTypes.radioChoiceEnum)) {
            attribute.isLoading = true;
            attribute.enum = [];
            try {
                const enumValues = await fetchDynamicListForQuestion(api.dataAccess, attribute.value, supportedGraphqlModels);
                attribute.enum = enumValues ?? [];
            } catch (err) {
                const id = err?.formatMessageId ?? 'dataAccessGeneralErrorMessage';
                snackbar.show(intl.formatMessage({ id }));
            }
            attribute.isLoading = false;
            updateEnums(x => !x);
        }
    };
    const onSave = () => {
        const contentAction = {
            [ConditionsSchema.effect]: contentActionDropDownValue,
        };
        onSaveProp && onSaveProp(query, contentAction);
    };
    const onQueryChange = query => {
        setQuery(query);
    };

    const initializeDropDown = () => {
        let dropDownOptions = Object.assign({}, contentActionDropDownOptions);
        let dropDownValue = contentActionDropDownValue;
        const isCase = (conditionIndex >= 0);
        if (isCase) {
            const condition = currentControl.conditions[conditionIndex];
            const currentConditionAffectValue = condition[ConditionsSchema.effect] ?? ContentActionOptions.show;
            const isMaxConiditions = (currentControl.conditions.length === 2);
            if (currentConditionAffectValue === ContentActionOptions.required) {
                dropDownValue = ContentActionOptions.required;
                if (isMaxConiditions) {
                    delete dropDownOptions[ContentActionOptions.show];
                    delete dropDownOptions[ContentActionOptions.hide];
                }
            } else {
                dropDownValue = currentConditionAffectValue;
                if (isMaxConiditions) {
                    delete dropDownOptions[ContentActionOptions.required];
                }
            }
        } else {
            if (currentControl?.conditions?.length > 0) {
                dropDownValue = ContentActionOptions.show;
                currentControl.conditions.map((condition) => {
                    const currentConditionAffectValue = condition[ConditionsSchema.effect] ?? ContentActionOptions.show;
                    if (currentConditionAffectValue === ContentActionOptions.required) {
                        delete dropDownOptions[ContentActionOptions.required];
                    } else {
                        delete dropDownOptions[ContentActionOptions.show];
                        delete dropDownOptions[ContentActionOptions.hide];
                        dropDownValue = ContentActionOptions.required;
                    }
                });
            }
        }
        if (selectedControl.controlType !== 'question' || currentControl?.required) {
            delete dropDownOptions[ContentActionOptions.required];
        }
        setContentActionDropDownOptions(dropDownOptions);
        setContentActionDropDownValue(dropDownValue);
    };

    const handleHelpClick = useCallback((event) => {
        const { currentTarget } = event;
        setAnchorEl(currentTarget ?? null);
    }, []);

    const handleHelpClickAway = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleDropDownOpen = useCallback(() => {
        setContentActionDropDownOpen(true);
    }, []);

    const handleDropDownClose = useCallback(() => {
        setContentActionDropDownOpen(false);
    }, []);

    const handleDropDownChange = useCallback((event) => {
        setContentActionDropDownValue(event?.target?.value);
    }, []);

    return (
        <Dialog
            classes={{
                paper: classes.paper,
            }}
            open={open}
            onClose={onClose}
            aria-labelledby="conditional-logic-dialog-title"
            aria-describedby="form-dialog-description"
            aria-modal="true"
        >
            <div id={'conditional-logict-title-container'} className={classes.titleContainer}>
                <DialogTitle id="conditional-logic-dialog-title">
                    <FormattedMessage id="conditionalLogicModalTitle" />
                </DialogTitle>
                <Typography>
                    <IconButton color="gray" id="conditional-logic-dialog-icon-button" aria-label={intl.formatMessage({ id: 'appDesignContextualHelpText' })} onClick={handleHelpClick}>
                        <Icon name="help" />
                    </IconButton>
                </Typography>
                <Popper
                    id={'conditional-logic-dialog-help-popper'}
                    className={classes.popper}
                    text={intl.formatMessage({ id: 'conditionalLogicHelp' })}
                    open={!!helpPopperAnchorEl}
                    anchorEl={helpPopperAnchorEl}
                    placement="right-start"
                    onClickAway={handleHelpClickAway}
                    transition={false}
                    arrow
                >
                </Popper>
            </div>
            <DialogContent>
                <AppDesignerSidebarDialogHeader
                    controlType={selectedControl.controlType === appTemplateControlTypes.question ? currentControl.type : selectedControl.controlType}
                    controlLabel={selectedControl.controlType === appTemplateControlTypes.question ? currentControl.label : currentControl.title}
                />
                <Divider />
                <div id={'conditional-logic-content-action-container'} className={classes.dropDownContainer}>
                    <div >
                        <Dropdown
                            id={'conditional-logic-content-action-dropdown'}
                            label={intl.formatMessage({ id: 'conditionalLogicContentActionDropdown' })}
                            onChange={handleDropDownChange}
                            value={contentActionDropDownValue ? contentActionDropDownValue : Object.values(contentActionDropDownOptions)[0]}
                            open={contentActionDropDownOpen}
                            onOpen={handleDropDownOpen}
                            onClose={handleDropDownClose}
                            disabled={Object.values(contentActionDropDownOptions).length === 1}
                        >
                            {Object.values(contentActionDropDownOptions).map((option, index) => {
                                return (
                                    <DropdownItem
                                        key={index}
                                        label={intl.formatMessage({ id: `${option?.VISIBILITY ?? option}` })}
                                        value={option}
                                    />
                                );
                            })}
                        </Dropdown>
                    </div>
                    <div>
                        <Typography>
                            <FormattedMessage id="ifAllConditionsAreMet" />
                        </Typography>
                    </div>
                </div>
                <QueryBuilder
                    fetchAttributeSuggestions={fetchAttributeSuggestions}
                    initialQuery={initialQuery}
                    onAttributeChange={populateAttributeForDynamicValues}
                    onQueryChange={onQueryChange}
                    strings={{
                        addRule: intl.formatMessage({ id: 'queryBuilder_addRule' }),
                        queryRule: {
                            backAttributeLabel: intl.formatMessage({ id: 'queryBuilder_queryRule_backAttributeLabel' }),
                            dateFormat: intl.formatMessage({ id: 'queryBuilder_queryRule_dateFormat' }),
                            defaultLookupValueLabel: intl.formatMessage({ id: 'queryBuilder_queryRule_defaultLookupValueLabel' }),
                            deleteAction: intl.formatMessage({ id: 'queryBuilder_queryRule_deleteAction' }),
                            deselectAction: intl.formatMessage({ id: 'queryBuilder_queryRule_deselectAction' }),
                            duplicateRuleAction: intl.formatMessage({ id: 'queryBuilder_queryRule_duplicateRuleAction' }),
                            editAction: intl.formatMessage({ id: 'queryBuilder_queryRule_editAction' }),
                            enterValue: intl.formatMessage({ id: 'queryBuilder_queryRule_enterValue' }),
                            lookupValuePlaceholder: intl.formatMessage({ id: 'queryBuilder_queryRule_lookupValuePlaceholder' }),
                            selectAction: intl.formatMessage({ id: 'queryBuilder_queryRule_selectAction' }),
                            selectAttribute: intl.formatMessage({ id: 'queryBuilder_queryRule_selectAttribute' }),
                            selectDate: intl.formatMessage({ id: 'queryBuilder_queryRule_selectDate' }),
                            selectOperator: intl.formatMessage({ id: 'queryBuilder_queryRule_selectOperator' }),
                            selectValue: intl.formatMessage({ id: 'queryBuilder_queryRule_selectValue' }),
                            checkLabel: intl.formatMessage({ id: 'queryBuilder_queryRule_checkLabel' }),
                            cancelLabel: intl.formatMessage({ id: 'queryBuilder_queryRule_cancelLabel' }),
                        },
                        queryRuleHeader: {
                            attribute: intl.formatMessage({ id: 'queryBuilder_queryRuleHeader_attribute' }),
                            operator: intl.formatMessage({ id: 'queryBuilder_queryRuleHeader_operator' }),
                            value: intl.formatMessage({ id: 'queryBuilder_queryRuleHeader_value' }),
                        },
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    <FormattedMessage id="actionCancel" />
                </Button>
                <Button onClick={onSave} color="primary">
                    <FormattedMessage id="done" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};
ConditionalLogicDialog.propTypes = {
    initialQuery: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func,
    open: PropTypes.bool.isRequired,
    conditionIndex: PropTypes.number,
};
export default ConditionalLogicDialog;
